import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { Home, ChevronRight, ChevronLeft, Search, Calendar, Tag, ArrowRight, User, Loader, Building2 } from 'lucide-react';
import { fetchBeritaList, fetchPengumumanUnit, getImageUrl, formatTanggalIndonesia } from '../utils/api';

const ITEMS_PER_PAGE = 9;

const KATEGORI_MAP = {
  akademik: { label: 'Akademik' },
  pengumuman: { label: 'Pengumuman' },
  kegiatan: { label: 'Kegiatan' },
  prestasi: { label: 'Prestasi' },
  umum: { label: 'Umum' },
};

const getCategoryLabel = (kategori) => {
  if (!kategori) return '';
  const mapped = KATEGORI_MAP[kategori.toLowerCase()];
  if (mapped) return mapped.label;
  return kategori.charAt(0).toUpperCase() + kategori.slice(1);
};

const Pagination = ({ current, total, onChange }) => {
  if (total <= 1) return null;
  const pages = [];
  const delta = 2;
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return (
    <div className="flex items-center justify-center gap-1 mt-10">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="p-2 rounded-lg disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
      >
        <ChevronLeft size={18} />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={i} className="px-2 text-gray-400">...</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
              current === p
                ? 'bg-[#7B2FBE] text-white'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        className="p-2 rounded-lg disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const ArticleThumbnail = ({ thumbnail, title, className, position }) => {
  const url = getImageUrl(thumbnail, { width: 800, height: 450 });
  if (url) {
    return (
      <img
        src={url}
        alt={title}
        loading="lazy"
        style={{ objectPosition: position || '50% 50%' }}
        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${className || ''}`}
      />
    );
  }
  return <div className="w-full h-full bg-gradient-to-br from-[#7B2FBE] to-[#1A1A2E]" />;
};

const Berita = () => {
  const [beritaList, setBeritaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Dua sumber: berita lama dari CMS dan pengumuman unit dari backend admin.
      // Keduanya memakai bentuk item yang sama lalu diurutkan bersama.
      const [berita, pengumuman] = await Promise.all([
        fetchBeritaList(),
        fetchPengumumanUnit().catch(() => []),
      ]);
      const merged = [...berita, ...pengumuman].sort(
        (a, b) => new Date(b.tanggal_publikasi || 0) - new Date(a.tanggal_publikasi || 0),
      );
      setBeritaList(merged);
      setLoading(false);
    };
    load();
  }, []);

  const categories = useMemo(() => {
    const rawCats = [...new Set(beritaList.map(a => a.kategori).filter(Boolean))].sort();
    const displayCats = rawCats.map(getCategoryLabel);
    const uniqueDisplayCats = [...new Set(displayCats)].sort();
    return ['Semua', ...uniqueDisplayCats];
  }, [beritaList]);

  const filtered = useMemo(() => beritaList.filter(a => {
    const displayCat = getCategoryLabel(a.kategori);
    const matchCat = activeCategory === 'Semua' || displayCat === activeCategory;
    const matchSearch = !search.trim() ||
      a.judul?.toLowerCase().includes(search.toLowerCase()) ||
      a.Excerpt?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  }), [beritaList, search, activeCategory]);

  const featuredArticle = useMemo(() =>
    filtered.find(a => a.featured) || filtered[0] || null, [filtered]);

  const restArticles = useMemo(() =>
    filtered.filter(a => a.id !== featuredArticle?.id), [filtered, featuredArticle]);

  const totalPages = Math.ceil(restArticles.length / ITEMS_PER_PAGE);
  const paginatedRest = restArticles.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  useEffect(() => { setPage(1); }, [search, activeCategory]);

  return (
    <div data-testid="berita-page">
      <SEO
        title="Berita & Informasi"
        description="Berita terbaru, pengumuman, dan informasi akademik dari STIKES Harapan Ibu Jambi. Tetap update dengan informasi kampus terkini."
      />
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <span className="text-white">Berita & Pengumuman</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Berita & Pengumuman</h1>
            <p className="text-white/80 max-w-xl text-base">Ikuti perkembangan terbaru kegiatan, prestasi, dan informasi penting STIKES HI Jambi.</p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="berita-search"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari berita..."
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`cat-${cat}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-[#7B2FBE] text-white'
                      : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-24">
              <Loader className="animate-spin text-[#7B2FBE]" size={36} />
              <span className="ml-3 text-gray-500 dark:text-gray-400">Memuat berita...</span>
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <Search size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Tidak ada berita yang sesuai.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <>
              {/* Featured */}
              {featuredArticle && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
                  <Link to={`/berita/${featuredArticle.slug}`} data-testid="article-featured" className="block group">
                    <div className="bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl transition-all duration-300 md:flex">
                      <div className="md:w-1/2 relative h-64 md:h-auto overflow-hidden">
                        <ArticleThumbnail thumbnail={featuredArticle.thumbnail} title={featuredArticle.judul} position={featuredArticle.thumbnailPosition} />
                        <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#7B2FBE] text-white text-xs font-bold">Terbaru</span>
                        {featuredArticle.unitLabel && (
                          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-3 py-1 text-xs font-bold text-white">
                            <Building2 size={12} /> Unit {featuredArticle.unitLabel}
                          </span>
                        )}
                      </div>
                      <div className="md:w-1/2 p-7 flex flex-col justify-center">
                         <span className="inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] font-semibold mb-3">
                           <Tag size={12} /> {getCategoryLabel(featuredArticle.kategori)}
                         </span>
                        <h2 className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-3 leading-tight group-hover:text-[#7B2FBE] transition-colors">{featuredArticle.judul}</h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-5">{featuredArticle.Excerpt}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                          <span className="flex items-center gap-1"><Calendar size={11} /> {formatTanggalIndonesia(featuredArticle.tanggal_publikasi)}</span>
                          {featuredArticle.penulis && (
                            <span className="flex items-center gap-1"><User size={11} /> {featuredArticle.penulis}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* Grid */}
              {paginatedRest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-4">
                  {paginatedRest.map((article, i) => (
                    <motion.div key={article.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.07 }}>
                      <Link to={`/berita/${article.slug}`} data-testid={`article-card-${article.id}`} className="block group h-full">
                        <div className="bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                          <div className="relative h-44 overflow-hidden">
                            <ArticleThumbnail thumbnail={article.thumbnail} title={article.judul} position={article.thumbnailPosition} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            {article.unitLabel && (
                              <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white">
                                <Building2 size={11} /> Unit {article.unitLabel}
                              </span>
                            )}
                          </div>
                          <div className="p-5 flex flex-col flex-1">
                             <span className="text-xs text-[#7B2FBE] font-semibold mb-2">
                               {getCategoryLabel(article.kategori)}
                             </span>
                            <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading text-sm leading-snug mb-3 flex-1 group-hover:text-[#7B2FBE] transition-colors">{article.judul}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 line-clamp-2">{article.Excerpt}</p>
                            <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-3">
                              <span className="flex items-center gap-1"><Calendar size={11} /> {formatTanggalIndonesia(article.tanggal_publikasi)}</span>
                              <span className="flex items-center gap-1.5 text-[#7B2FBE]">Baca <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              <Pagination
                current={page}
                total={totalPages}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Berita;
